import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const PRICES = {
  lunar: {
    photo: 3,
    video: 40,
    video_narrated: 50,
  },
  estrela: {
    photo: 3,
    video: 25,
    video_narrated: 30,
  },
  basic: {
    photo: 6,
    video: 40,
    video_narrated: 50,
  },
  dependent_estrela: {
    photo: 8,
    video: 25,
    video_narrated: 30,
  },
  dependent_lunar: {
    photo: 6,
    video: 25,
    video_narrated: 30,
  },
  regular: {
    photo: 12, // changed from 8
    video: 40,
    video_narrated: 50,
  },
  guest: {
    photo: 12,
    video: 60,
    video_narrated: 75,
  },
} as const;

export const useSubscription = () => {
  const { user, profile } = useAuth();
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [planId, setPlanId] = useState<"lunar" | "estrela" | "dependent_lunar" | "dependent_estrela" | "basic" | "regular" | "guest">("guest");
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);

  const refreshSubscription = useCallback(async () => {
    if (!user) {
      setIsSubscriber(false);
      setPlanId("guest");
      setLoading(false);
      return;
    }

    // 1. Check direct subscription in DB FIRST across all records to get accurate status
    const { data: allSubs } = await supabase
      .from("subscriptions")
      .select("*, plan_id")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    const activeSub = (allSubs || []).find((s: any) => 
      (s.status === 'active' || s.status === 'canceled') && (!s.expires_at || new Date(s.expires_at) > new Date())
    );

    if (activeSub) {
      setIsSubscriber(true);
      setPlanId(activeSub.plan_id || "estrela");
      setSubscription(activeSub);
      setLoading(false);
      return;
    }

    // NOTE: profile.is_vip is used ONLY for UI badges/labels.
    // It does NOT grant subscriber pricing. Only a real active subscription record does.
    // This prevents admins from accidentally giving free discounts by marking users as VIP.
    
          // Se nǜo tem assinatura ativa prpria, checar se Ǹ dependente
      if (user.email) {
        const { data: depData } = await supabase
          .from("subscription_dependents")
          .select("main_subscriber_id")
          .eq("dependent_email", user.email)
          .maybeSingle();
          
        if (depData?.main_subscriber_id) {
          const { data: allMainSubs } = await supabase
            .from("subscriptions")
            .select("*, plan_id")
            .eq("user_id", depData.main_subscriber_id)
            .order("created_at", { ascending: false });
            
          const mainSub = (allMainSubs || []).find((s: any) => 
            (s.status === 'active' || s.status === 'canceled') && (!s.expires_at || new Date(s.expires_at) > new Date())
          );
          
          // Checar se o titular tem assinatura ativa ou cancelada mas vigente
          const isMainActive = mainSub && (mainSub.status === 'active' || mainSub.status === 'canceled') && (!mainSub.expires_at || new Date(mainSub.expires_at) > new Date());
          if (isMainActive && mainSub?.plan_id === 'estrela') {
            setIsSubscriber(true); // Tratamos dependente como VIP para liberar descontos
            setPlanId(`dependent_${mainSub.plan_id}` as any);
            setSubscription({ ...mainSub, is_dependent: true });
            setLoading(false);
            return;
          }
        }
      }

      // LEGACY FIX: Some old Cakto users have is_vip = true but NO subscription record.
      // We must treat them as Estrela subscribers.
      if (profile?.is_vip && (!profile.vip_until || new Date(profile.vip_until) > new Date())) {
        setIsSubscriber(true);
        setPlanId("estrela");
        setSubscription({ status: "active", plan_id: "estrela", expires_at: profile.vip_until, is_legacy: true });
        setLoading(false);
        return;
      }

setIsSubscriber(false);
    setPlanId("regular");
    setSubscription(null);
    setLoading(false);
  }, [user, profile]);

  useEffect(() => {
    refreshSubscription();
    if (!user) return;

    // Subscribe to real-time changes
    const channelName = `sub-changes-${user.id}-${Math.random()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          console.log("Detectada mudança na assinatura, atualizando...");
          refreshSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, profile, refreshSubscription]);

  const getPrice = (mediaType: string, mediaSubtype?: string | null, isSelectedForUser?: boolean) => {
    // O desconto VIP SÓ se aplica se a foto estiver atribuída ao usuário (isSelectedForUser = true)
    let effectivePlanId: keyof typeof PRICES = "regular";
    
    if (isSubscriber) {
      if (isSelectedForUser) {
        effectivePlanId = planId; // Aplica o desconto do plano
      } else {
        effectivePlanId = "regular"; // Preço avulso (sem desconto) para fotos de outros
      }
    } else {
      effectivePlanId = planId === "guest" ? "guest" : "regular";
    }

    const tier = PRICES[effectivePlanId] || PRICES.regular;
    if (mediaType === "video" && mediaSubtype === "narrated") return tier.video_narrated;
    if (mediaType === "video") return tier.video;
    return tier.photo;
  };

  const formatPrice = (price: number) =>
    `R$ ${price.toFixed(2).replace(".", ",")}`;

  return { isSubscriber, planId, loading, subscription, getPrice, formatPrice, refreshSubscription };
};
